/**
 * Stickers personalizables de Japón.
 *
 * Las imágenes base se sirven desde el backend protegido via
 * GET /api/custom-stickers/:countryId/assets/:filename
 * `baseImage` contiene solo el nombre del archivo; la URL completa
 * se resuelve en runtime con token de autenticación.
 *
 * Ver `src/data/customStickers/README.md` para el shape completo y cómo
 * añadir más stickers.
 */

const japan = {
	id: "japan",
	name: "Japón",
	flag: "🇯🇵",
	accentColor: "#E84A5F",
	description:
		"Stickers kawaii de Japón. Añade tu nombre y personalízalos al vuelo.",
	stickers: [
		{
			id: "japan-kawaii-badge",
			title: "Ema japonesa",
			baseImage: "kawaii-badge.png",
			aspectRatio: 1,
			text: {
				area: {
					xPercent: 0.5,
					yPercent: 0.61,
					/* La franja blanca real es algo más estrecha que el centro del PNG */
					widthPercent: 0.6,
					heightPercent: 0.2,
				},
				align: "center",
				defaultText: "Tu nombre",
				placeholder: "Tu nombre",
				maxChars: 18,
				fontFamily: "'Fredoka', sans-serif",
				fontWeight: 400,
				fontStyle: "normal",
				letterSpacing: 0.3,
				uppercase: false,
				capitalize: false,
				minFontSize: 9,
				maxFontSize: 30,
				/* Margen extra ya va en fitFontSize vía strokeFitFactor + strokeWidth */
				widthSafety: 0.9,
				fontSizeSteps: [
					{ maxChars: 4, size: 30 },
					{ maxChars: 6, size: 28 },
					{ maxChars: 8, size: 24 },
					{ maxChars: 10, size: 19 },
					{ maxChars: 14, size: 14 },
					{ maxChars: 18, size: 12 },
				],
				color: "#f0687b",
			},
		},
		{
			id: "japan-passport-stamp",
			title: "Sello pasaporte",
			baseImage: "passport-stamp.png",
			aspectRatio: 1,
			text: {
				area: {
					xPercent: 0.5,
					yPercent: 0.23,
					widthPercent: 1,
					heightPercent: 0.8,
				},
				align: "center",
				defaultText: "Tu nombre",
				placeholder: "Tu nombre",
				maxChars: 12,
				// Titan One sin stroke, estética de sello real
				fontFamily: "'Kids Marker', cursive",
				fontWeight: 400,
				fontStyle: "normal",
				letterSpacing: 1,
				uppercase: false,
				capitalize: false,
				minFontSize: 12,
				maxFontSize: 34,
				fontSizeSteps: [
					{ maxChars: 4, size: 34 },
					{ maxChars: 6, size: 28 },
					{ maxChars: 8, size: 22 },
					{ maxChars: 12, size: 18 },
				],
				color: "#d23430",
				stroke: null,
				strokeWidth: 0,
				shadow: null,
			},
		},
		{
			id: "japan-billete-banner",
			title: "Billete Shinkansen",
			baseImage: "billete.png",
			aspectRatio: 1,
			text: {
				area: {
					xPercent: 0.5,
					yPercent: 0.65,
					widthPercent: 0.5,
					heightPercent: 0.12,
				},
				align: "center",
				defaultText: "Tu nombre",
				placeholder: "Tu nombre",
				maxChars: 12,
				fontFamily: "'Fredoka', sans-serif",
				fontWeight: 400,
				fontStyle: "normal",
				letterSpacing: 0,
				uppercase: false,
				capitalize: false,
				/**
				 * Si minFontSize es alto, fitFontSize acaba usando scaleX y el texto se estira.
				 * Mejor bajar la fuente de verdad hasta ~8px antes de comprimir horizontalmente.
				 */
				minFontSize: 8,
				maxFontSize: 56,
				fontSizeSteps: [
					{ maxChars: 4, size: 52 },
					{ maxChars: 6, size: 44 },
					{ maxChars: 8, size: 36 },
					{ maxChars: 10, size: 28 },
					{ maxChars: 12, size: 22 },
				],
				color: "#D9243B",
				/*stroke: "#ffffff",
				strokeWidth: 7,
				shadow: {
					color: "rgba(0,0,0,0.18)",
					offsetX: 0,
					offsetY: 3,
					blur: 6,
				},*/
			},
		},
		{
			id: "japan-tokyo-travel-tag",
			title: "Tag Maleta Japón",
			baseImage: "tokyo-travel-tag.png",
			aspectRatio: 1,
			text: {
				area: {
					xPercent: 0.5,
					yPercent: 0.6,
					/* La línea roja escrita en el arte es más estrecha que el centro del PNG */
					widthPercent: 0.62,
					heightPercent: 0.16,
				},
				align: "center",
				defaultText: "Tu nombre",
				placeholder: "Tu nombre",
				maxChars: 18,
				/* Oniely mide ~10–15% más ancha en DOM que en canvas al cargar la fuente */
				fontFamily: "'Oniely', cursive",
				fontWeight: 400,
				fontStyle: "normal",
				letterSpacing: 0,
				uppercase: false,
				capitalize: false,
				minFontSize: 8,
				maxFontSize: 34,
				widthSafety: 0.82,
				fontSizeSteps: [
					{ maxChars: 4, size: 34 },
					{ maxChars: 6, size: 28 },
					{ maxChars: 9, size: 21 },
					{ maxChars: 12, size: 15 },
					{ maxChars: 18, size: 11 },
				],
				color: "#d23430",

				/*shadow: {
					color: "rgba(0,0,0,0.18)",
					offsetX: 0,
					offsetY: 1,
					blur: 2,
				},*/
			},
		},
		{
			id: "japan-tokyo-postal-tag",
			title: "Postal Japón",
			baseImage: "postal.png",
			aspectRatio: 1,
			text: {
				area: {
					/*
					 * Antes: centro 0.7 + mitad ancho 0.37 ⇒ salía del lienzo por la derecha.
					 * El hueco para firmar está abajo-derecha pero dentro del PNG.
					 */
					xPercent: 0.7,
					yPercent: 0.7,
					widthPercent: 0.52,
					heightPercent: 0.14,
				},
				align: "center",
				defaultText: "Tu nombre",
				placeholder: "Tu nombre",
				maxChars: 18,
				fontFamily: "'Oniely', cursive",
				fontWeight: 400,
				fontStyle: "normal",
				letterSpacing: 0,
				uppercase: false,
				capitalize: false,
				minFontSize: 8,
				maxFontSize: 34,
				widthSafety: 0.82,
				fontSizeSteps: [
					{ maxChars: 4, size: 34 },
					{ maxChars: 6, size: 28 },
					{ maxChars: 9, size: 21 },
					{ maxChars: 12, size: 15 },
					{ maxChars: 18, size: 11 },
				],
				color: "#d23430",

				/*shadow: {
					color: "rgba(0,0,0,0.18)",
					offsetX: 0,
					offsetY: 1,
					blur: 2,
				},*/
			},
		},
		{
			id: "japan-badge-tag",
			title: "Medalla Japón",
			baseImage: "medalla-japon.png",
			aspectRatio: 1,
			text: {
				area: {
					xPercent: 0.46,
					/* Un poco más arriba: los descendentes no cruzan el borde inferior del metal */
					yPercent: 0.8,
					widthPercent: 0.58,
					heightPercent: 0.13,
				},
				align: "center",
				defaultText: "Tu nombre",
				placeholder: "Tu nombre",
				maxChars: 18,
				fontFamily: "'Oniely', cursive",
				fontWeight: 400,
				fontStyle: "normal",
				letterSpacing: 0,
				uppercase: false,
				capitalize: false,
				minFontSize: 6,
				maxFontSize: 22,
				/* Oniely en DOM suele ser más ancha que measureText en canvas */
				widthSafety: 0.76,
				fontSizeSteps: [
					{ maxChars: 4, size: 18 },
					{ maxChars: 6, size: 16 },
					{ maxChars: 9, size: 13 },
					{ maxChars: 12, size: 11 },
					{ maxChars: 15, size: 9 },
					{ maxChars: 18, size: 8 },
				],
				color: "#d23430",
				curveEndsMarginRatio: 0.04,
				// Nombre siguiendo el lazo inferior (misma idea que Medalla 2026).
				curve: {
					type: "arc",
					archHeight: 0.1,
				},
				/*shadow: {
					color: "rgba(0,0,0,0.18)",
					offsetX: 0,
					offsetY: 1,
					blur: 2,
				},*/
			},
		},
		{
			id: "animal-japan-tag",
			title: "Shiba Inu",
			baseImage: "animal.png",
			aspectRatio: 0.72,
			text: {
				area: {
					/*
					 * Ancho ~cartel central. Alto suficiente: si heightPercent es muy
					 * bajo, fitFontSize limita por altura (fontSize*1.15) y nombres
					 * cortos salen diminutos.
					 */
					xPercent: 0.48,
					yPercent: 0.7,
					widthPercent: 0.46,
					heightPercent: 0.15,
				},
				align: "center",
				defaultText: "Tu nombre",
				placeholder: "Tu nombre",
				maxChars: 18,
				fontFamily: "'Fredoka', sans-serif",
				fontWeight: 500,
				fontStyle: "normal",
				letterSpacing: 0.15,
				uppercase: true,
				capitalize: false,
				minFontSize: 7,
				maxFontSize: 26,
				widthSafety: 0.9,
				fontSizeSteps: [
					{ maxChars: 5, size: 26 },
					{ maxChars: 9, size: 15 },
					{ maxChars: 12, size: 12 },
					{ maxChars: 13, size: 10 },
					{ maxChars: 18, size: 9 },
				],
				color: "#5C3A1E",
				shadow: {
					color: "rgba(0,0,0,0.3)",
					offsetX: 0,
					offsetY: 1,
					blur: 1,
				},
			},
		},
		{
			id: "badge-2026",
			title: "Medalla 2026",
			baseImage: "badge-2026.png",
			aspectRatio: 0.72,
			text: {
				area: {
					xPercent: 0.49,
					yPercent: 0.84,
					/* Más ancho ⇒ arco más largo (mejor para nombres largos en la cinta) */
					widthPercent: 0.36,
					/* Bajo: limita fontSize por altura y evita “” gigante en la cinta */
					heightPercent: 0.16,
				},
				align: "center",
				defaultText: "Tu nombre",
				placeholder: "Tu nombre",
				maxChars: 18,
				fontFamily: "'Fredoka', sans-serif",
				fontWeight: 400,
				fontStyle: "normal",
				letterSpacing: 0,
				uppercase: false,
				capitalize: false,
				minFontSize: 6,
				maxFontSize: 17,
				widthSafety: 0.88,
				curveEndsMarginRatio: 0.04,
				fontSizeSteps: [
					{ maxChars: 4, size: 15 },
					{ maxChars: 6, size: 13 },
					{ maxChars: 9, size: 11 },
					{ maxChars: 12, size: 9 },
					{ maxChars: 18, size: 8 },
				],
				color: "#d75056",
				// El nombre sigue la curva del lazo/banner: leve sonrisa
				// (cóncavo hacia arriba), peak en el centro por debajo.
				curve: {
					type: "arc",
					archHeight: 0.2,
				},
			},
		},
		{
			id: "amuleto-japon",
			title: "Omamori",
			baseImage: "amuleto.png",
			aspectRatio: 0.72,
			text: {
				area: {
					xPercent: 0.51,
					yPercent: 0.585,
					widthPercent: 0.145,
					heightPercent: 0.43,
				},
				align: "center",
				defaultText: "Tu nombre",
				placeholder: "Tu nombre",
				maxChars: 18,
				vertical: true,
				/* Línea horizontal girada 90° (como en el diseño), no letra a letra en columna */
				verticalRotate: true,
				/* Un poco más bajo el box de línea ⇒ permite subir más el px en la ranura estrecha */
				verticalLineHeight: 1.08,
				verticalRotateInnerPad: 0.016,
				fontFamily: '"Fredoka", sans-serif',
				fontWeight: 400,
				fontStyle: "normal",
				letterSpacing: 0,
				uppercase: false,
				capitalize: false,
				minFontSize: 5,
				maxFontSize: 22,
				widthSafety: 0.99,
				verticalHeightSafety: 1,
				fontSizeSteps: [
					{ maxChars: 5, size: 20 },
					{ maxChars: 9, size: 20 },
					{ maxChars: 14, size: 20 },
					{ maxChars: 18, size: 18 },
				],
				color: "#552500",
				/*shadow: {
					color: "rgba(0,0,0,0.18)",
					offsetX: 0,
					offsetY: 1,
					blur: 2,
				},*/
			},
		},
		{
			id: "fluent-in-ramen",
			title: "Ramen",
			baseImage: "fluent-in-ramen.png",
			aspectRatio: 0.72,
			text: {
				area: {
					xPercent: 0.49,
					yPercent: 0.89,
					/* Caja ancha: sin esto fitFontSize baja mucho el px aunque subas fontSizeSteps. */
					widthPercent: 0.5,
					heightPercent: 0.42,
				},
				align: "center",
				defaultText: "Tu nombre",
				placeholder: "Tu nombre",
				maxChars: 18,
				// Caveat Brush: pincel manuscrito, sensación etiqueta
				fontFamily: "'Fredoka', sans-serif",
				fontWeight: 400,
				fontStyle: "normal",
				letterSpacing: 0,
				uppercase: false,
				capitalize: false,
				minFontSize: 6,
				maxFontSize: 20,
				widthSafety: 0.94,
				fontSizeSteps: [
					{ maxChars: 4, size: 16 },
					{ maxChars: 8, size: 15 },
					{ maxChars: 12, size: 14 },
					{ maxChars: 15, size: 13 },
					{ maxChars: 16, size: 14 },
					{ maxChars: 18, size: 14 },
				],
				color: "#552500",
				/*shadow: {
					color: "rgba(0,0,0,0.18)",
					offsetX: 0,
					offsetY: 1,
					blur: 2,
				},*/
			},
		},
		{
			id: "sushi-lover",
			title: "Sushi",
			baseImage: "sushi-lover.png",
			aspectRatio: 0.72,
			text: {
				area: {
					xPercent: 0.49,
					yPercent: 0.8,
					widthPercent: 0.5,
					heightPercent: 0.42,
				},
				align: "center",
				defaultText: "Tu nombre",
				placeholder: "Tu nombre",
				maxChars: 18,
				fontFamily: "'Fredoka', sans-serif",
				fontWeight: 400,
				fontStyle: "normal",
				letterSpacing: 0,
				uppercase: false,
				capitalize: false,
				minFontSize: 6,
				maxFontSize: 20,
				widthSafety: 0.94,
				fontSizeSteps: [
					{ maxChars: 4, size: 16 },
					{ maxChars: 8, size: 15 },
					{ maxChars: 12, size: 14 },
					{ maxChars: 15, size: 13 },
					{ maxChars: 16, size: 14 },
					{ maxChars: 18, size: 14 },
				],
				color: "#552500",
				/*shadow: {
					color: "rgba(0,0,0,0.18)",
					offsetX: 0,
					offsetY: 1,
					blur: 2,
				},*/
			},
		},
		{
			id: "passport-japon",
			title: "Pasaporte",
			baseImage: "passport.png",
			aspectRatio: 0.72,
			text: {
				area: {
					xPercent: 0.51,
					yPercent: 0.53,
					widthPercent: 0.33,
					heightPercent: 0.4,
				},
				align: "center",
				defaultText: "Tu nombre",
				placeholder: "Tu nombre",
				maxChars: 18,
				fontFamily: "'Oniely', cursive",
				fontWeight: 400,
				fontStyle: "normal",
				letterSpacing: 0,
				uppercase: false,
				capitalize: false,
				/* minFontSize < último paso de fontSizeSteps: si coinciden, fitFontSize no
				   baja el px (bucle no entra) y solo comprime con scaleX ⇒ letras aplastadas. */
				minFontSize: 7,
				// Techo global: solo recorta si es menor que el paso elegido en fontSizeSteps.
				maxFontSize: 20,
				widthSafety: 0.8,
				fontSizeSteps: [
					{ maxChars: 4, size: 20 },
					{ maxChars: 8, size: 18 },
					{ maxChars: 12, size: 15 },
					{ maxChars: 16, size: 13 },
					{ maxChars: 18, size: 12 },
				],
				color: "#552500",
				/*shadow: {
					color: "rgba(0,0,0,0.18)",
					offsetX: 0,
					offsetY: 1,
					blur: 2,
				},*/
			},
		},
		{
			id: "matcha-lover",
			title: "Matcha lover",
			baseImage: "matcha-lover.png",
			aspectRatio: 0.72,
			text: {
				area: {
					xPercent: 0.49,
					yPercent: 0.85,
					widthPercent: 0.52,
					heightPercent: 0.44,
				},
				align: "center",
				defaultText: "Tu nombre",
				placeholder: "Tu nombre",
				maxChars: 18,
				fontFamily: "'Fredoka', sans-serif",
				fontWeight: 400,
				fontStyle: "normal",
				letterSpacing: 0,
				uppercase: false,
				capitalize: false,
				minFontSize: 6,
				maxFontSize: 24,
				widthSafety: 0.78,
				fontSizeSteps: [
					{ maxChars: 4, size: 18 },
					{ maxChars: 8, size: 17 },
					{ maxChars: 12, size: 16 },
					{ maxChars: 15, size: 15 },
					{ maxChars: 16, size: 16 },
					{ maxChars: 18, size: 17 },
				],
				color: "#552500",
				/*shadow: {
					color: "rgba(0,0,0,0.18)",
					offsetX: 0,
					offsetY: 1,
					blur: 2,
				},*/
			},
		},
		{
			id: "maleta-japon",
			title: "Maleta",
			baseImage: "maleta.png",
			aspectRatio: 1,
			text: {
				area: {
					xPercent: 0.72,
					yPercent: 0.5,
					widthPercent: 0.4,
					heightPercent: 0.4,
				},
				align: "center",
				defaultText: "Tu nombre",
				placeholder: "Tu nombre",
				maxChars: 18,
				fontFamily: "'Fredoka', sans-serif",
				fontWeight: 400,
				fontStyle: "normal",
				letterSpacing: 0,
				uppercase: false,
				capitalize: false,
				minFontSize: 5,
				maxFontSize: 16,
				minScaleX: 0.82,
				widthSafety: 0.6,
				fontSizeSteps: [
					{ maxChars: 6, size: 15 },
					{ maxChars: 9, size: 11 },
					{ maxChars: 11, size: 8 },
					{ maxChars: 12, size: 7 },
					{ maxChars: 18, size: 6 },
				],
				color: "#552500",
				/*shadow: {
					color: "rgba(0,0,0,0.18)",
					offsetX: 0,
					offsetY: 1,
					blur: 2,
				},*/
			},
		},
		{
			id: "suica-japon",
			title: "Suica",
			baseImage: "suica.png",
			aspectRatio: 1,
			text: {
				area: {
					xPercent: 0.7,
					yPercent: 0.34,
					widthPercent: 0.44,
					heightPercent: 0.4,
				},
				align: "center",
				defaultText: "Tu nombre",
				placeholder: "Tu nombre",
				maxChars: 18,
				fontFamily: "'Fredoka', sans-serif",
				fontWeight: 400,
				fontStyle: "normal",
				letterSpacing: 0,
				uppercase: false,
				capitalize: false,
				minFontSize: 6,
				maxFontSize: 20,
				minScaleX: 0.92,
				widthSafety: 0.76,
				fontSizeSteps: [
					{ maxChars: 6, size: 20 },
					{ maxChars: 9, size: 17 },
					{ maxChars: 11, size: 14 },
					{ maxChars: 12, size: 13 },
					{ maxChars: 18, size: 11 },
				],
				color: "#552500",
				/*shadow: {
					color: "rgba(0,0,0,0.18)",
					offsetX: 0,
					offsetY: 1,
					blur: 2,
				},*/
			},
		},
	],
};

export default japan;
